import React, { useEffect, useRef } from "react";
import * as d3 from "d3";
import { matrixTransformation } from "../util/matrixTransformation";
import { bradleyTerry } from "../util/bradleyTerry";
import { thurstoneMosteller } from "../util/thurstoneMosteller";

const HEADER_BG = "#374151";
const CARD     = { borderRadius: "8px", marginTop: "16px", width: "fit-content", boxShadow: "0 2px 6px rgba(0,0,0,0.08)" };
const CARD_HDR = { background: HEADER_BG, padding: "8px 14px", display: "flex", alignItems: "center", gap: "8px", borderRadius: "8px 8px 0 0" };
const SECTION  = { borderLeft: "1px solid #cbd5e1", borderRight: "1px solid #cbd5e1" };
const FOOTER   = { ...SECTION, borderBottom: "1px solid #cbd5e1", borderRadius: "0 0 8px 8px", padding: "5px 14px 6px", background: "#f9fafb", borderTop: "1px solid #e5e7eb", fontSize: "11px", color: "#6b7280", display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "center" };

function normalPhi(x) {
    const ax  = Math.abs(x);
    const t   = 1 / (1 + 0.2316419 * ax);
    const pdf = Math.exp(-0.5 * ax * ax) / Math.sqrt(2 * Math.PI);
    const poly = ((((1.330274429 * t - 1.821255978) * t + 1.781477937) * t - 0.356563782) * t + 0.319381530) * t;
    return x >= 0 ? 1 - pdf * poly : pdf * poly;
}

export default function ResidualPlot({ data, modelType }) {
    const wrapperRef   = useRef(null);
    const containerRef = useRef(null);

    useEffect(() => {
        if (!data || !data.twoAfcData) return;

        function render() {
            if (!containerRef.current || !wrapperRef.current) return;

            const totalW = Math.max(400, wrapperRef.current.clientWidth - 32);

            const transformed = matrixTransformation(data);
            const methods     = transformed.sortedMethods;
            const N           = methods.length;
            const model       = modelType === "BT" ? bradleyTerry(transformed) : thurstoneMosteller(transformed);
            const betas       = model.betas;

            function predictedProb(i, j) {
                return modelType === "BT"
                    ? 1 / (1 + Math.exp(betas[j] - betas[i]))
                    : normalPhi((betas[i] - betas[j]) / Math.sqrt(2));
            }

            const matrix = transformed.matrix;
            const points = [];
            for (let i = 0; i < N; i++) {
                for (let j = i + 1; j < N; j++) {
                    const nij = matrix[i][j];
                    const nji = matrix[j][i];
                    const n   = nij + nji;
                    if (n === 0) continue;
                    const pObs  = nij / n;
                    const pFit  = predictedProb(i, j);
                    const resid = pObs - pFit;
                    points.push({
                        label: `${methods[i]} vs ${methods[j]}`,
                        pFit, pObs, resid, n,
                        outlier: Math.abs(resid) > 0.10
                    });
                }
            }

            if (points.length === 0) return;

            const margin = { top: 30, right: 40, bottom: 55, left: 65 };
            const innerW = totalW - margin.left - margin.right;
            const innerH = 320;
            const totalH = innerH + margin.top + margin.bottom;

            const xScale = d3.scaleLinear().domain([0, 1]).range([0, innerW]);

            const residMax = Math.max(
                Math.abs(d3.min(points, d => d.resid)),
                Math.abs(d3.max(points, d => d.resid)),
                0.12
            ) * 1.25;
            const yScale = d3.scaleLinear()
                .domain([-residMax, residMax])
                .range([innerH, 0])
                .nice();

            const svg = d3.create("svg")
                .attr("width", totalW)
                .attr("height", totalH);

            const g = svg.append("g")
                .attr("transform", `translate(${margin.left},${margin.top})`);

            // horizontal grid lines
            g.selectAll("line.hgrid")
                .data(yScale.ticks(6))
                .join("line")
                .attr("class", "hgrid")
                .attr("x1", 0).attr("x2", innerW)
                .attr("y1", d => yScale(d)).attr("y2", d => yScale(d))
                .attr("stroke", "#e5e7eb").attr("stroke-width", 1);

            // vertical grid lines
            g.selectAll("line.vgrid")
                .data(xScale.ticks(10))
                .join("line")
                .attr("class", "vgrid")
                .attr("x1", d => xScale(d)).attr("x2", d => xScale(d))
                .attr("y1", 0).attr("y2", innerH)
                .attr("stroke", "#e5e7eb").attr("stroke-width", 1);

            g.append("line")
                .attr("x1", 0).attr("x2", innerW)
                .attr("y1", yScale(0)).attr("y2", yScale(0))
                .attr("stroke", "#dc2626")
                .attr("stroke-width", 1.8)
                .attr("stroke-dasharray", "7,4");

            g.append("g")
                .attr("transform", `translate(0,${innerH})`)
                .call(d3.axisBottom(xScale).ticks(10).tickFormat(d3.format(".1f")))
                .selectAll("text").attr("font-size", "11px");

            g.append("g")
                .call(d3.axisLeft(yScale).ticks(6).tickFormat(d => d >= 0 ? `+${d.toFixed(2)}` : d.toFixed(2)))
                .selectAll("text").attr("font-size", "11px");

            g.append("text")
                .attr("x", innerW / 2).attr("y", innerH + 42)
                .attr("text-anchor", "middle")
                .attr("font-size", "12px").attr("fill", "#374151")
                .text("Fitted Values: Predicted P(A > B)");

            g.append("text")
                .attr("transform", `rotate(-90)`)
                .attr("x", -innerH / 2).attr("y", -50)
                .attr("text-anchor", "middle")
                .attr("font-size", "12px").attr("fill", "#374151")
                .text("Residuals (Observed vs Predicted)");

            // tooltip
            const tooltip = d3.select("body").append("div")
                .style("position", "absolute")
                .style("pointer-events", "none")
                .style("background", "rgba(15,23,42,0.88)")
                .style("color", "white")
                .style("font-size", "11px")
                .style("padding", "6px 10px")
                .style("border-radius", "6px")
                .style("line-height", "1.6")
                .style("opacity", 0)
                .style("z-index", 9999);

            g.selectAll("circle")
                .data(points)
                .join("circle")
                .attr("cx", d => xScale(d.pFit))
                .attr("cy", d => yScale(d.resid))
                .attr("r", d => d.outlier ? 7 : 5.5)
                .attr("fill", "#374151")
                .attr("opacity", 0.75)
                .attr("stroke", "white")
                .attr("stroke-width", 1.2)
                .style("cursor", "pointer")
                .on("mouseover", function(event, d) {
                    d3.select(this).attr("r", d.outlier ? 9 : 7.5).attr("opacity", 1);
                    tooltip.transition().duration(120).style("opacity", 1);
                    tooltip.html(
                        `<strong>${d.label}</strong><br/>` +
                        `Observed:  ${d.pObs.toFixed(3)}<br/>` +
                        `Predicted: ${d.pFit.toFixed(3)}<br/>` +
                        `Residual:  ${d.resid >= 0 ? "+" : ""}${d.resid.toFixed(3)}<br/>` +
                        `n = ${d.n} comparisons`
                    );
                })
                .on("mousemove", function(event) {
                    tooltip
                        .style("left", (event.pageX + 14) + "px")
                        .style("top",  (event.pageY - 28) + "px");
                })
                .on("mouseout", function(event, d) {
                    d3.select(this).attr("r", d.outlier ? 7 : 5.5).attr("opacity", 0.75);
                    tooltip.transition().duration(200).style("opacity", 0);
                });

            g.selectAll("text.pt-label")
                .data(points)
                .join("text")
                .attr("class", "pt-label")
                .attr("x", d => xScale(d.pFit))
                .attr("y", d => yScale(d.resid) + (d.resid >= 0 ? 18 : -9))
                .attr("text-anchor", "middle")
                .attr("font-size", "9px")
                .attr("fill", "#374151")
                .style("stroke", "white").style("stroke-width", "2.5px")
                .style("stroke-linejoin", "round").style("paint-order", "stroke")
                .text(d => d.label);

            containerRef.current.innerHTML = "";
            containerRef.current.appendChild(svg.node());

            return () => tooltip.remove();
        }

        const cleanup = render();
        let rafId;
        const ro = new ResizeObserver(() => {
            cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(render);
        });
        if (wrapperRef.current) ro.observe(wrapperRef.current);
        return () => { ro.disconnect(); cancelAnimationFrame(rafId); cleanup && cleanup(); };
    }, [data, modelType]);

    const modelTag = modelType === "BT" ? "Bradley–Terry" : "Thurstone–Mosteller";

    return (
        <div style={CARD}>
            <div style={CARD_HDR}>
                <h4 style={{ margin: 0, color: "white", fontSize: "13px", fontWeight: "600" }}>Residual Plot</h4>
                <span style={{color: "rgba(255,255,255,0.8)", fontSize: "10px", padding: "1px 6px", borderRadius: "4px" }}>
                    {modelTag}
                </span>
            </div>

            <div style={{ ...SECTION, padding: "4px 14px", background: "#f9fafb", borderBottom: "1px solid #e5e7eb", fontSize: "11px", color: "#6b7280" }}>
                Good model fit = residuals scattered around zero. Above zero line = model underpredicts. Below zero line = model overpredicts.
            </div>

            <div ref={wrapperRef} style={{ ...SECTION, padding: "12px 16px 8px", background: "white" }}>
                <div ref={containerRef} />
            </div>

            <div style={FOOTER}>
                <span>Hover dots for details</span>
                <span>|</span>
                <span style={{ display: "inline-flex", alignItems: "center", gap: "5px" }}>
                    <span style={{ width: "10px", height: "10px", borderRadius: "50%", background: "#374151", display: "inline-block" }} />
                    method pair
                </span>
                <span>|</span>
                <span>larger dot = |residual| &gt; 0.10</span>
            </div>
        </div>
    );
}
